import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './PerformanceManagement.css';
import { SEC_ApiCall, SEC_DataApiPost } from '../func.js';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../common/header.js';

const UNAUTHORISED_MESSAGE = "You don’t have access to Performance Management.";
const GENERIC_ERROR_MESSAGE = "We couldn’t load performance data. Please try again.";

// Landing dashboard for Performance Management. Serves as the starting point for upcoming module features.
export default function PerformanceManagementDashboard() {
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isUnauthorized, setIsUnauthorized] = useState(false);
    const [profiles, setProfiles] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [filteredTemplates, setFilteredTemplates] = useState([]);
    const [selectedProfileId, setSelectedProfileId] = useState('');
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [emptyProfiles, setEmptyProfiles] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionError, setSubmissionError] = useState('');
    const [dashboardLoading, setDashboardLoading] = useState(true);
    const [dashboardError, setDashboardError] = useState(false);
    const [activeRecords, setActiveRecords] = useState([]);
    const [inactiveRecords, setInactiveRecords] = useState([]);
    const [managerRecords, setManagerRecords] = useState([]);

    const showContinue = Boolean(selectedProfileId && selectedTemplateId);
    const modalTitle = "New Performance Management record";
    const toastConfig = { autoClose: 4000, position: 'top-right' };

    const showTemplateNotice = useMemo(() => {
        return !isLoading && !hasError && !isUnauthorized && selectedProfileId && filteredTemplates.length === 0;
    }, [filteredTemplates.length, hasError, isLoading, isUnauthorized, selectedProfileId]);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    async function fetchDashboardData() {
        setDashboardLoading(true);
        setDashboardError(false);
        try {
            const response = await SEC_ApiCall('perfmgr/dashboard');

            if (response?.message) {
                setDashboardError(true);
                if (response.message === 'Unauthorized') {
                    setIsUnauthorized(true);
                }
                setDashboardLoading(false);
                return;
            }

            const activeList = Array.isArray(response?.active) ? response.active : [];
            const inactiveList = Array.isArray(response?.inactive) ? response.inactive : [];

            setActiveRecords(activeList);
            setInactiveRecords(inactiveList);
            try {
                const managerResponse = await SEC_ApiCall('perfmgr/manager-dashboard');
                setManagerRecords(Array.isArray(managerResponse?.records) ? managerResponse.records : []);
            } catch (managerError) {
                console.log('Performance Management manager dashboard unavailable', managerError);
            }
            setIsUnauthorized(false);
            setDashboardLoading(false);
        } catch (error) {
            console.log('Performance Management dashboard data error', error);
            setDashboardError(true);
            setDashboardLoading(false);
        }
    }

    function handleSelectRecord(record) {
        const targetId = record?.recordId;
        if (!targetId) {
            return;
        }
        navigate(`/app/performance-management/record/${targetId}`, {
            state: {
                profileName: record.profileName || ''
            }
        });
    }

    const renderRecordCollection = (records, emptyLabel, variant = 'active') => {
        if (dashboardLoading) {
            return (
                <div className="performance-management-dashboard__records-loading">
                    <span className="performance-management-dashboard__records-spinner" aria-hidden="true"></span>
                    <span>Loading records.</span>
                </div>
            );
        }

        if (dashboardError) {
            return (
                <p className="performance-management-dashboard__records-error">
                    We couldn't load records. Please try again later.
                </p>
            );
        }

        if (!records || records.length === 0) {
            return (
                <p className="performance-management-dashboard__records-empty">
                    {emptyLabel}
                </p>
            );
        }

        return records.map(record => (
            <button
                type="button"
                className={`performance-management-dashboard__record-button${variant === 'inactive' ? ' performance-management-dashboard__record-button--inactive' : ''}`}
                key={`${record.profileId || 'profile'}-${record.recordId || 'record'}`}
                onClick={() => handleSelectRecord(record)}
                disabled={!record.recordId}
            >
                <span className="performance-management-dashboard__record-name">
                    {record.profileName}
                </span>
            </button>
        ));
    };

    function handleOpenModal() {
        setSelectedProfileId('');
        setSelectedTemplateId('');
        setFilteredTemplates([]);
        setEmptyProfiles(false);
        setHasError(false);
        setIsUnauthorized(false);
        setSubmissionError('');
        setIsSubmitting(false);
        setIsModalOpen(true);
        fetchBootstrapData();
    }

    function handleCloseModal() {
        setIsModalOpen(false);
        setIsSubmitting(false);
        setSubmissionError('');
    }

    // Fetches bootstrap data for profiles and templates whenever the modal is opened.
    async function fetchBootstrapData() {
        setIsLoading(true);
        setHasError(false);
        setIsUnauthorized(false);
        try {
            const response = await SEC_ApiCall('perfmgr/bootstrap');

            if (response?.message) {
                setHasError(true);
                if (response.message === 'Unauthorized') {
                    setIsUnauthorized(true);
                }
                setIsLoading(false);
                return;
            }

            const profileList = Array.isArray(response?.profiles) ? response.profiles : [];
            const templateList = Array.isArray(response?.templates) ? response.templates : [];

            setProfiles(profileList);
            setTemplates(templateList);
            setFilteredTemplates(templateList);
            setEmptyProfiles(profileList.length === 0);
            setIsLoading(false);
        } catch (error) {
            console.log('Performance Management bootstrap error', error);
            setHasError(true);
            setIsUnauthorized(false);
            setIsLoading(false);
        }
    }

    function handleProfileChange(event) {
        const value = event.target.value;
        setSelectedProfileId(value);
        setSelectedTemplateId('');
        setSubmissionError('');
        setIsSubmitting(false);

        if (!value) {
            setFilteredTemplates(templates);
            return;
        }

        const selectedProfile = profiles.find(profile => profile.profileId === value);

        if (selectedProfile && Array.isArray(selectedProfile.templates)) {
            const allowedTemplateIds = new Set(selectedProfile.templates);
            const filtered = templates.filter(template => allowedTemplateIds.has(template.templateId));
            setFilteredTemplates(filtered);
            return;
        }

        setFilteredTemplates(templates);
    }

    function handleTemplateChange(event) {
        setSubmissionError('');
        setSelectedTemplateId(event.target.value);
    }

    async function handleCreateRecord() {
        if (!showContinue || isSubmitting) {
            return;
        }

        setSubmissionError('');
        setIsSubmitting(true);

        const payload = {
            profileId: selectedProfileId,
            templateId: selectedTemplateId,
            objectiveCount: templates.find((template) => template.templateId === selectedTemplateId)?.maxObjectives || 1
        };

        const notifyError = (message) => {
            setSubmissionError(message);
            toast.error(`Error: ${message}`, toastConfig);
        };

        try {
            const response = await SEC_DataApiPost(
                'perfmgr/records',
                JSON.stringify(payload),
                {},
                () => notifyError('We could not create the record. Please try again.')
            );

            if (!response) {
                notifyError('We could not create the record. Please try again.');
                setIsSubmitting(false);
                return;
            }

            if (response.message) {
                if (response.message === 'Unauthorized') {
                    setIsUnauthorized(true);
                    notifyError(UNAUTHORISED_MESSAGE);
                } else {
                    notifyError(response.message);
                }
                setIsSubmitting(false);
                return;
            }

            setIsSubmitting(false);
            setHasError(false);
            setIsUnauthorized(false);
            setIsModalOpen(false);
            setSelectedProfileId('');
            setSelectedTemplateId('');
            setFilteredTemplates(templates);
            setSubmissionError('');
            await fetchDashboardData();
            toast.success('Performance record created', toastConfig);
        } catch (error) {
            console.log('Performance Management record creation error', error);
            notifyError('We could not create the record. Please try again.');
            setIsSubmitting(false);
        }
    }

    if (isUnauthorized) {
        return (
            <div className="performance-management-dashboard">
                <div className="performance-management-no-access">
                    <h2 className="performance-management-no-access__heading">Performance Management</h2>
                    <p className="performance-management-no-access__copy">{UNAUTHORISED_MESSAGE}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="performance-management-dashboard">
            <Header
                title="Performance Management"
                subtitle="Manage your objectives, monitoring reviews, final review and professional development in one annual record."
                rightContent={
                    <button
                        type="button"
                        className="module-page-header__button"
                        onClick={handleOpenModal}
                    >
                        Add new record
                    </button>
                }
            />

            <section className="performance-management-dashboard__records">
                <div className="performance-management-dashboard__records-section">
                    <h2 className="performance-management-dashboard__records-title">Active records</h2>
                    <div className="performance-management-dashboard__records-list">
                        {renderRecordCollection(activeRecords, 'No active records yet.', 'active')}
                    </div>
                </div>
                <div className="performance-management-dashboard__records-section performance-management-dashboard__records-section--inactive">
                    <h2 className="performance-management-dashboard__records-title">Inactive records</h2>
                    <div className="performance-management-dashboard__records-list">
                        {renderRecordCollection(inactiveRecords, 'No inactive records.', 'inactive')}
                    </div>
                </div>
            </section>

            {managerRecords.length > 0 && <section className="performance-management-dashboard__records">
                <div className="performance-management-dashboard__records-section">
                    <h2 className="performance-management-dashboard__records-title">My staff awaiting attention</h2>
                    <div className="performance-management-dashboard__records-list">
                        {managerRecords.map((record) => <button type="button" className="performance-management-dashboard__record-button" key={record.recordId} onClick={() => handleSelectRecord(record)}>
                            <span className="performance-management-dashboard__record-name">{record.staffName} · {String(record.status || '').replaceAll('_', ' ')}</span>
                        </button>)}
                    </div>
                </div>
            </section>}

            <section className="performance-management-dashboard__content">
                <article className="performance-management-dashboard__card">
                    <h2 className="performance-management-dashboard__card-title">Getting Started</h2>
                    <p className="performance-management-dashboard__card-copy">
                        Select Add new record, choose the available academic year and an assigned template, then complete and submit your objectives.
                    </p>
                </article>

                <article className="performance-management-dashboard__card">
                    <h2 className="performance-management-dashboard__card-title">During the Year</h2>
                    <p className="performance-management-dashboard__card-copy">
                        Reopen your active record for each monitoring review, keep your CPD log up to date, and complete the final review and staff sign-off at the end of the cycle.
                    </p>
                </article>
            </section>

            {isModalOpen && (
                <div className="performance-management-modal">
                    <div className="performance-management-modal__overlay" onClick={handleCloseModal} role="presentation"></div>
                    <div className="performance-management-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="pm-modal-title">
                        <div className="performance-management-modal__header">
                            <h2 id="pm-modal-title" className="performance-management-modal__title">{modalTitle}</h2>
                            <button
                                type="button"
                                className="performance-management-modal__close"
                                onClick={handleCloseModal}
                                aria-label="Close modal"
                            >
                                ×
                            </button>
                        </div>

                        <div className="performance-management-modal__body">
                            {isLoading && (
                                <div className="performance-management-modal__loading">
                                    <span className="performance-management-modal__spinner" aria-hidden="true"></span>
                                    <span>Loading data…</span>
                                </div>
                            )}

                            {!isLoading && hasError && (
                                <div className="performance-management-modal__error">
                                    <p>{isUnauthorized ? UNAUTHORISED_MESSAGE : GENERIC_ERROR_MESSAGE}</p>
                                    {!isUnauthorized && (
                                        <button
                                            type="button"
                                            className="performance-management-modal__retry"
                                            onClick={fetchBootstrapData}
                                        >
                                            Retry
                                        </button>
                                    )}
                                </div>
                            )}

                            {!isLoading && !hasError && emptyProfiles && (
                                <p className="performance-management-modal__empty-notice">
                                    No profiles available. Contact admin.
                                </p>
                            )}

                            {!isLoading && !hasError && !emptyProfiles && (
                                <>
                                    <label className="performance-management-modal__label" htmlFor="pm-profile-select">
                                        Academic year (Profile)
                                    </label>
                                    <select
                                        id="pm-profile-select"
                                        className="performance-management-modal__select"
                                        value={selectedProfileId}
                                        onChange={handleProfileChange}
                                    >
                                        <option value="">Select academic year</option>
                                        {profiles.map(profile => (
                                            <option key={profile.profileId} value={profile.profileId}>
                                                {profile.profileName}
                                            </option>
                                        ))}
                                    </select>

                                    <label className="performance-management-modal__label" htmlFor="pm-template-select">
                                        Template
                                    </label>
                                    <select
                                        id="pm-template-select"
                                        className="performance-management-modal__select"
                                        value={selectedTemplateId}
                                        onChange={handleTemplateChange}
                                        disabled={filteredTemplates.length === 0}
                                    >
                                        <option value="">
                                            {filteredTemplates.length === 0 ? 'No templates available' : 'Select template'}
                                        </option>
                                        {filteredTemplates.map(template => (
                                            <option key={template.templateId} value={template.templateId}>
                                                {template.name}
                                            </option>
                                        ))}
                                    </select>

                                    {showTemplateNotice && (
                                        <p className="performance-management-modal__empty-notice">
                                            No templates are assigned to the selected academic year. Contact admin.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="performance-management-modal__footer">
                            {submissionError && (
                                <p className="performance-management-modal__submit-error" role="alert">
                                    {submissionError}
                                </p>
                            )}
                            <button
                                type="button"
                                className="performance-management-modal__secondary"
                                onClick={handleCloseModal}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="performance-management-modal__primary"
                                onClick={handleCreateRecord}
                                disabled={!showContinue || hasError || isUnauthorized || emptyProfiles || isLoading || isSubmitting}
                            >
                                {isSubmitting ? 'Creating...' : 'Continue'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
